import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '../client';
import type {
    OAuthConsentInfoResponse,
    SubmitOAuthConsentRequest,
    SubmitOAuthConsentResponse,
} from '../types';
import { CONNECTED_APPS_KEY } from './useConnectedApps';

/**
 * Loads the client name, redirect hosts and requested scopes for a consent screen.
 * Pass null to hold the request back, for example while the user is not logged in yet.
 */
export function useOAuthConsentInfo(
    clientId: string | null
): UseQueryResult<OAuthConsentInfoResponse, Error> {
    return useQuery({
        queryKey: ['oauthConsentInfo', clientId],
        queryFn: () =>
            api.get<OAuthConsentInfoResponse>(
                `/api/oauth/consent-info?client_id=${encodeURIComponent(clientId ?? '')}`
            ),
        enabled: clientId !== null && clientId.length > 0,
        // Consent is a one-shot decision, so a cached answer is never reused.
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/**
 * Stores the user's approve or deny decision for one client.
 */
export function useSubmitOAuthConsent(): UseMutationResult<
    SubmitOAuthConsentResponse,
    Error,
    SubmitOAuthConsentRequest
> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: SubmitOAuthConsentRequest) =>
            api.post<SubmitOAuthConsentResponse>('/api/oauth/consent', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONNECTED_APPS_KEY });
        },
    });
}
